import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { Tool } from "@app/tool/model/tool.model";
import { User } from "@app/shared/models";
import {
    buildAutoGeneratedToolCode,
    CODING_TYPE_NA_OPTION,
    CODING_TYPE_OPTIONS,
    CodingTypeOption,
    CodingTypeValue,
    isAutoGeneratedToolCodeCodingType,
    isNACodingType,
    normalizeCodingTypeValue
} from '@app/shared/models/enums/codingType.enum';
import { ToolService } from '@app/core/services/tool.service';
import { ToolPropertyValidationState } from '@app/tool/model/tool-property-validation-state.enum';
import { ToolValidationConflictTool } from '@app/tool/model/tool-validation-conflict-tool.model';
import { ToolPropertyValidationResult } from '@app/tool/model/tool-property-validation-result.model';
import { debounceTime, switchMap, tap } from 'rxjs/operators';
import { merge, of } from 'rxjs';
import { ToolProperty } from '@app/tool/model/enums/tool-property.enum';
import { ToolPropertyValidationInfoModalComponent } from '@app/tool/components/tool-property-validation-info-modal/tool-property-validation-info-modal.component';

@Component({
    selector: 'app-description-form',
    templateUrl: './description-form.component.html',
    styleUrls: ['./description-form.component.scss']
})
export class DescriptionFormComponent implements OnInit, OnChanges {
    @ViewChild('infoModal', { static: true }) infoModal: ToolPropertyValidationInfoModalComponent;

    @Input() showValidations = false;
    @Input() owners: User[];
    @Input() tool: Tool;

    descriptionForm: UntypedFormGroup;
    codingTypeOptions: CodingTypeOption[] = CODING_TYPE_OPTIONS;
    infoModalTools: ToolValidationConflictTool[] = [];
    private acknowledgedInfoSignatures = new Set<string>();
    private pendingInfoSignature: string = null;
    private initialValidationSignature: string = null;

    constructor(private cdr: ChangeDetectorRef,
                private toolService: ToolService) {

    }

    ngOnInit() {
        this.descriptionForm = new UntypedFormGroup({
            name: new UntypedFormControl(this.tool.name),
            description: new UntypedFormControl(this.tool.details.description),
            isSpare: new UntypedFormControl(this.tool.details.isSpare),
            mesCode: new UntypedFormControl(this.tool.mesCode),
            supplierCode: new UntypedFormControl(this.tool.supplierCode),
            inventoryNumber: new UntypedFormControl(this.tool.inventoryNumber),
            toolControlName: new UntypedFormControl(this.tool.toolControlName),
            codingType: new UntypedFormControl(null),
            owner: new UntypedFormControl(this.tool.owner),
            idFromSet: new UntypedFormControl(this.tool.idFromSet)
        });

        this.loadFormValues(this.tool);
        this.bindToolCodeValidation();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['tool'] && this.tool && this.descriptionForm) {
            this.loadFormValues(this.tool);
        }
    }

    isFormValid() {
        return this.descriptionForm.valid;
    }

    reset() {
        this.descriptionForm.reset();
    }

    value() {
        const formValue = this.descriptionForm.value;
        const codingType = formValue.codingType?.value ?? formValue.codingType ?? 'N/A';

        return {
            ...formValue,
            codingType
        };
    }

    private loadFormValues(tool: Tool) {
        const selectedCodingType = this.getCodingTypeOption(normalizeCodingTypeValue(tool.codingType));

        this.descriptionForm.patchValue({
            name: tool.name,
            description: tool.details.description,
            isSpare: tool.details.isSpare,
            mesCode: isNACodingType(selectedCodingType.value)
                ? 'N/A'
                : isAutoGeneratedToolCodeCodingType(selectedCodingType.value)
                    ? buildAutoGeneratedToolCode(tool.toolUid)
                    : tool.mesCode,
            supplierCode: tool.supplierCode,
            inventoryNumber: tool.inventoryNumber,
            toolControlName: tool.toolControlName,
            codingType: selectedCodingType,
            owner: tool.owner,
            idFromSet: tool.idFromSet
        }, { emitEvent: false });

        this.initialValidationSignature = this.buildValidationSignature();
        this.pendingInfoSignature = null;
        this.infoModalTools = [];

        this.cdr.detectChanges();
    }

    private bindToolCodeValidation() {
        const mesCodeCtrl = this.descriptionForm.get('mesCode')!;
        const codingTypeCtrl = this.descriptionForm.get('codingType')!;

        merge(
            mesCodeCtrl.valueChanges.pipe(
                debounceTime(500)
            ),
            codingTypeCtrl.valueChanges
        )
            .pipe(
                tap(() => this.resetInfoAcknowledgement()),
                switchMap(() => this.validateToolCode())
            )
            .subscribe((isValid) => {
                this.applyServerValidationResult(isValid);
            });

        codingTypeCtrl.valueChanges.subscribe(() => {
            this.syncToolCodeWithCodingType();
        });
    }

    canReopenInfoModal(): boolean {
        return !!this.descriptionForm.get('mesCode')?.errors?.toolCodeInfoPending && !!this.infoModalTools.length;
    }

    isToolCodeReadonly(): boolean {
        return isNACodingType(this.getCodingTypeValue()) || this.isToolCodeAutoGenerated();
    }

    isToolCodeAutoGenerated(): boolean {
        return isAutoGeneratedToolCodeCodingType(this.getCodingTypeValue());
    }

    reopenInfoModal() {
        if (!this.canReopenInfoModal()) {
            return;
        }

        this.pendingInfoSignature = this.buildValidationSignature();
        this.infoModal.open();
    }

    private validateToolCode() {
        const mesCodeCtrl = this.descriptionForm.get('mesCode')!;
        const trimmedCode = this.normalizeCode(mesCodeCtrl.value);
        const codingType = this.getCodingTypeValue();

        this.removeError(mesCodeCtrl, 'toolCodeNotUnique');
        this.removeError(mesCodeCtrl, 'toolCodeInfoPending');

        if (!trimmedCode || isNACodingType(codingType) || isAutoGeneratedToolCodeCodingType(codingType)) {
            return of(null);
        }

        if (this.buildValidationSignature() === this.initialValidationSignature) {
            return of(null);
        }

        const equipmentIds = (this.tool?.details?.equipment || [])
            .map(equipment => equipment?.id)
            .filter(id => !!id);

        if (!equipmentIds.length) {
            return of(null);
        }

        return this.toolService.validateToolProperty(ToolProperty.MESCode, trimmedCode, {
            codingType,
            equipmentIds,
            toolId: this.tool?.id
        });
    }

    private applyServerValidationResult(result: ToolPropertyValidationResult | null) {
        const mesCodeCtrl = this.descriptionForm.get('mesCode')!;
        const signature = this.buildValidationSignature();

        if (result === null) {
            return;
        }

        switch (result.validationState) {
            case ToolPropertyValidationState.Valid:
                this.removeError(mesCodeCtrl, 'toolCodeNotUnique');
                this.removeError(mesCodeCtrl, 'toolCodeInfoPending');
                return;
            case ToolPropertyValidationState.Info:
                if (this.acknowledgedInfoSignatures.has(signature)) {
                    this.removeError(mesCodeCtrl, 'toolCodeInfoPending');
                    return;
                }
                this.setError(mesCodeCtrl, 'toolCodeInfoPending');
                this.infoModalTools = result.tools || [];
                this.pendingInfoSignature = signature;
                this.infoModal.open();
                return;
            case ToolPropertyValidationState.Error:
                this.removeError(mesCodeCtrl, 'toolCodeInfoPending');
                this.setError(mesCodeCtrl, 'toolCodeNotUnique', {
                    tools: this.buildToolsLabel(result.tools || [])
                });
                return;
            default:
                this.removeError(mesCodeCtrl, 'toolCodeNotUnique');
        }
    }

    private getCodingTypeValue(): CodingTypeValue {
        const rawValue = this.descriptionForm.get('codingType')?.value?.value
            ?? this.descriptionForm.get('codingType')?.value
            ?? 'N/A';
        return normalizeCodingTypeValue(rawValue);
    }

    private getCodingTypeOption(value: CodingTypeValue): CodingTypeOption {
        return this.codingTypeOptions.find(opt => opt.value === value) || CODING_TYPE_NA_OPTION;
    }

    private normalizeCode(value: string): string {
        return value?.trim() || '';
    }

    private syncToolCodeWithCodingType() {
        const mesCodeCtrl = this.descriptionForm.get('mesCode')!;
        const codingType = this.getCodingTypeValue();
        const isNA = isNACodingType(codingType);
        const isAutoGenerated = isAutoGeneratedToolCodeCodingType(codingType);

        if (isNA) {
            mesCodeCtrl.setValue('N/A', { emitEvent: false });
            this.removeError(mesCodeCtrl, 'toolCodeNotUnique');
            this.removeError(mesCodeCtrl, 'toolCodeInfoPending');
            return;
        }

        if (isAutoGenerated) {
            mesCodeCtrl.setValue(buildAutoGeneratedToolCode(this.tool?.toolUid), { emitEvent: false });
            this.removeError(mesCodeCtrl, 'toolCodeNotUnique');
            this.removeError(mesCodeCtrl, 'toolCodeInfoPending');
            return;
        }

        if (this.normalizeCode(mesCodeCtrl.value) === 'N/A' || this.normalizeCode(mesCodeCtrl.value) === buildAutoGeneratedToolCode(this.tool?.toolUid)) {
            mesCodeCtrl.setValue('', { emitEvent: false });
        }
    }

    onInfoConfirm() {
        if (!this.pendingInfoSignature) {
            return;
        }

        this.acknowledgedInfoSignatures.add(this.pendingInfoSignature);
        this.removeError(this.descriptionForm.get('mesCode')!, 'toolCodeInfoPending');
        this.pendingInfoSignature = null;
        this.infoModalTools = [];
    }

    private resetInfoAcknowledgement() {
        this.acknowledgedInfoSignatures.clear();
        this.removeError(this.descriptionForm.get('mesCode')!, 'toolCodeInfoPending');
        this.pendingInfoSignature = null;
        this.infoModalTools = [];
    }

    private buildValidationSignature(): string {
        return JSON.stringify({
            mesCode: this.normalizeCode(this.descriptionForm.get('mesCode')?.value),
            codingType: this.getCodingTypeValue(),
            equipmentIds: (this.tool?.details?.equipment || []).map(equipment => equipment?.id).filter(id => !!id),
            toolId: this.tool?.id
        });
    }

    private buildToolsLabel(tools: ToolValidationConflictTool[]): string {
        return tools
            .map(tool => `${tool.toolUid} - ${tool.name}`)
            .join(', ');
    }

    private setError(control: AbstractControl, key: string, value: any = true) {
        control.setErrors({
            ...(control.errors || {}),
            [key]: value
        });
    }

    private removeError(control: AbstractControl, key: string) {
        const errors = { ...(control.errors || {}) };
        delete errors[key];
        control.setErrors(Object.keys(errors).length ? errors : null);
    }
}
